'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocale, useTranslations } from 'next-intl';
import { Check, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button/button';
import { PasswordInput } from '@/components/ui/input/password-input';
import { Label } from '@/components/ui/label/label';

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(8, 'Password must be at least 8 characters'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

type ResetPasswordState = 'idle' | 'submitting' | 'success' | 'token_error' | 'error';

function getErrorMessage(
  code: string | null,
  t: ReturnType<typeof useTranslations<'auth.resetPassword'>>,
): string {
  switch (code) {
    case 'INVALID_TOKEN':
      return t('tokenExpired');
    case 'PASSWORD_TOO_SHORT':
      return t('passwordTooShort');
    default:
      return t('errorDescription');
  }
}

export function ResetPasswordPage() {
  const t = useTranslations('auth.resetPassword');
  const locale = useLocale();
  const localePath = (path: string) => (locale === 'en' ? path : `/${locale}${path}`);
  const searchParams = useSearchParams();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const token = searchParams.get('token');
  const callbackError = searchParams.get('error');
  const [state, setState] = useState<ResetPasswordState>(callbackError ? 'token_error' : 'idle');
  const [errorCode, setErrorCode] = useState<string | null>(callbackError);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors: _errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onChange',
  });

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setState('token_error');
      setErrorCode('INVALID_TOKEN');
      return;
    }

    setState('submitting');
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: data.password }),
        credentials: 'include',
      });

      if (res.ok) {
        setState('success');
        return;
      }

      const body = await res.json().catch(() => null);
      const code = (body as { code?: string })?.code ?? null;

      if (code === 'INVALID_TOKEN') {
        setState('token_error');
        setErrorCode(code);
      } else {
        setState('error');
        setErrorCode(code);
      }
    } catch {
      setState('error');
      setErrorCode(null);
    }
  };

  if (!token || state === 'token_error') {
    return (
      <div
        className="notranslate flex min-h-[80vh] items-center justify-center bg-stone-950 p-6"
        translate="no"
      >
        <div className="max-w-md w-full rounded-xl border border-stone-800 bg-stone-900 p-8 text-center">
          <XCircle className="w-10 h-10 text-red-500 mx-auto" />
          <h1 className="text-xl font-semibold text-white mt-3">{t('error')}</h1>
          <p className="text-stone-400 text-sm mt-2">{getErrorMessage(errorCode, t)}</p>
          <Button asChild variant="outline" className="mt-4">
            <a href={localePath('/forgot-password')}>{t('tryAgain')}</a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="notranslate flex min-h-[80vh] items-center justify-center bg-stone-950 p-6"
      translate="no"
    >
      <div className="max-w-md w-full rounded-xl border border-stone-800 bg-stone-900 p-8">
        {state === 'success' ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
            <h1 className="text-xl font-semibold text-white">{t('success')}</h1>
            <p className="text-stone-400 text-sm">{t('successDescription')}</p>
            <Button asChild variant="outline" className="mt-4">
              <a href={localePath('/login')}>{t('backToLogin')}</a>
            </Button>
          </div>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-white mb-2">{t('title')}</h1>
            <p className="text-stone-400 text-sm mb-6">{t('description')}</p>

            <form
              onSubmit={(e) => {
                void handleSubmit(onSubmit)(e);
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="password" className="text-stone-300">
                  {t('passwordLabel')}
                </Label>
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  {...register('password')}
                  disabled={state === 'submitting'}
                  className="bg-stone-800 border-stone-700 text-white"
                />
                {mounted && password !== undefined && password.length > 0 && (
                  <div className="flex items-center gap-1.5 text-xs">
                    {password.length >= 8 ? (
                      <>
                        <Check className="h-3 w-3 text-green-500" />
                        <span className="text-green-500">{t('minChars')}</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-3 w-3 text-red-400" />
                        <span className="text-red-400">
                          {t('minCharsCount', { count: password.length })}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-stone-300">
                  {t('confirmPasswordLabel')}
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  placeholder="••••••••"
                  {...register('confirmPassword')}
                  disabled={state === 'submitting'}
                  className="bg-stone-800 border-stone-700 text-white"
                />
                {mounted && confirmPassword !== undefined && confirmPassword.length > 0 && (
                  <div className="flex items-center gap-1.5 text-xs">
                    {password === confirmPassword ? (
                      <>
                        <Check className="h-3 w-3 text-green-500" />
                        <span className="text-green-500">{t('passwordsMatch')}</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-3 w-3 text-red-400" />
                        <span className="text-red-400">{t('passwordsNoMatch')}</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {state === 'error' && (
                <div className="rounded-md bg-red-900/20 border border-red-800/30 p-3 text-sm text-red-400">
                  {getErrorMessage(errorCode, t)}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={
                  state === 'submitting' ||
                  !password ||
                  password.length < 8 ||
                  password !== confirmPassword
                }
              >
                {state === 'submitting' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('submitting')}
                  </>
                ) : (
                  t('submit')
                )}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
